/**
 * Builds the block that goes at the top of the PDF's first page: the app header and the
 * codesheet metadata bar, stripped of every button, plus a line naming who exported the file.
 *
 * The sources are CLONED into an off-screen container rather than captured where they live.
 * The app header sits inside a `position: fixed` wrapper, which html2canvas offsets by the
 * scroll position, and the two bars are not adjacent in the DOM anyway. Cloning also means the
 * live page never flickers while the buttons are removed.
 */

/** Marks a subtree that exists for the screen only and is dropped from the clone. */
export const PDF_OMIT_SELECTOR = '[data-pdf-omit]'

export type PdfHeaderOptions = {
  /** Elements to clone, top to bottom. Missing ones are skipped. */
  sources: Array<HTMLElement | null>
  /** The "Exported by ..." line. Skipped when empty. */
  note?: string
  /** Container width in CSS px -- pass the table width so the header scales identically. */
  widthPx: number
}

/**
 * Creates the header element and mounts it off-screen so html2canvas can rasterize it.
 * The caller owns it and must call `.remove()` once the capture is done.
 */
export function createPdfHeaderElement({ sources, note, widthPx }: PdfHeaderOptions): HTMLElement {
  const container = document.createElement('div')
  container.setAttribute('data-pdf-header', '')
  // Off-screen but still laid out: `display: none` or `visibility: hidden` would capture blank.
  container.style.position = 'fixed'
  container.style.left = '-10000px'
  container.style.top = '0'
  container.style.zIndex = '-1'
  container.style.backgroundColor = '#ffffff'
  container.style.width = `${widthPx}px`

  for (const source of sources) {
    if (!source) continue
    const clone = source.cloneNode(true) as HTMLElement
    clone.removeAttribute('id')
    clone.querySelectorAll(PDF_OMIT_SELECTOR).forEach((node) => node.remove())
    container.appendChild(clone)
  }

  if (note) {
    const line = document.createElement('div')
    line.className = 'px-6 py-2 text-xs text-[#666]'
    line.textContent = note
    container.appendChild(line)
  }

  document.body.appendChild(container)
  return container
}
